import { useState } from 'react';
import { trpc } from '../../api/trpc';
import { useDialogClose } from '../../hooks/useDialogClose';

interface NewProjectDialogProps {
  onClose: () => void;
  onCreated: (projectId: string) => void;
}

export function NewProjectDialog({ onClose, onCreated }: NewProjectDialogProps) {
  const { isClosing, handleClose } = useDialogClose(onClose);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createProject = trpc.projects.create.useMutation();
  const utils = trpc.useUtils();

  const handleCreate = async () => {
    if (!title.trim()) return;
    setError(null);
    try {
      const project = await createProject.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        customInstructions: customInstructions.trim() || undefined,
      });
      void utils.projects.list.invalidate();
      onCreated(project.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project. Is the project service running?');
    }
  };

  return (
    <div className={`delete-dialog-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
      <form className={`proj-dialog${isClosing ? ' dialog-closing' : ''}`} onClick={e => e.stopPropagation()} onSubmit={e => { e.preventDefault(); void handleCreate(); }}>
        <div className="proj-dialog-title">New Project</div>

        <div className="proj-field">
          <label>Title *</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g., Incident Response Playbooks"
            autoFocus
            maxLength={100}
          />
        </div>

        <div className="proj-field">
          <label>Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What is this project about?"
            maxLength={2000}
          />
        </div>

        <div className="proj-field">
          <label>Custom Instructions</label>
          <textarea
            value={customInstructions}
            onChange={e => setCustomInstructions(e.target.value)}
            placeholder="Instructions for Cade when working in this project..."
            maxLength={5000}
          />
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius)',
            background: 'var(--sev-high-bg)', color: 'var(--sev-high)', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <div className="proj-dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={handleClose}>Cancel</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!title.trim() || createProject.isPending}
          >
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  );
}
